import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "../../lib/auth-context";
import { useI18n, LANGUAGES } from "../../lib/i18n";
import { api } from "../../lib/api";
import MobileHeader from "../../components/MobileHeader";
import TrustedTraderBadge from "../../components/TrustedTraderBadge";
import { Building2, FileText, Users, Briefcase, Bell, ShieldCheck, HelpCircle, LogOut, ChevronRight, Receipt, Layers, Globe, Check, Award } from "lucide-react";

const ITEMS = [
    { icon: Building2, label: "Business Information", to: "/business/more" },
    { icon: Layers, label: "MerchantOS", to: "/business/more/merchantos" },
    { icon: FileText, label: "Invoices", to: "/business/more/invoices" },
    { icon: Users, label: "Customers", to: "/business/more/customers" },
    { icon: Briefcase, label: "Suppliers", to: "/business/more/suppliers" },
    { icon: Receipt, label: "Expenses", to: "/business/more/expenses" },
    { icon: Bell, label: "Notification Settings", to: "/business/more" },
    { icon: ShieldCheck, label: "Security", to: "/business/more" },
    { icon: HelpCircle, label: "Support", to: "/business/more" },
];

export default function BusinessProfile() {
    const { user, logout } = useAuth();
    const { lang, setLang, t } = useI18n();
    const nav = useNavigate();
    const [langOpen, setLangOpen] = useState(false);
    const [tt, setTt] = useState(null);

    useEffect(() => {
        if (user?.role === "business") {
            api.get("/splitpay/trusted-trader/me").then((r) => setTt(r.data)).catch(() => {});
        }
    }, [user]);

    if (!user) return null;
    const currentLang = LANGUAGES.find((l) => l.code === lang) || LANGUAGES[0];

    return (
        <div data-testid="business-profile-page">
            <MobileHeader title="More" backTo="/business" variant="blue">
                <div className="mt-4 bg-white/15 backdrop-blur rounded-2xl p-4 flex items-center gap-3">
                    <div className="size-12 rounded-xl bg-white/20 flex items-center justify-center">
                        <Building2 size={22} />
                    </div>
                    <div className="text-white flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                            <div className="font-display text-lg font-semibold">{user.business_name}</div>
                            {tt?.is_trusted_trader && <TrustedTraderBadge size="xs" withTooltip={false} />}
                        </div>
                        <div className="text-xs text-white/80">RC: 1234567 · Corporate Account</div>
                    </div>
                </div>
            </MobileHeader>
            <div className="px-5 -mt-3 pb-4 space-y-3">
                {/* Trusted Trader status card */}
                {tt && (
                    <TrustedTraderStatusCard tt={tt} />
                )}                <div className="bg-white rounded-2xl border border-slate-200/60 product-shadow overflow-hidden">
                    {ITEMS.map((it, i) => (
                        <Link
                            to={it.to}
                            key={it.label}
                            data-testid={`biz-more-${it.label.replace(/\s/g, "")}`}
                            className={`w-full flex items-center gap-3 px-4 py-3.5 ${i !== ITEMS.length - 1 ? "border-b border-slate-100" : ""}`}
                        >
                            <div className="size-9 rounded-lg bg-slate-100 flex items-center justify-center text-slate-600 flex-shrink-0">
                                <it.icon size={18} />
                            </div>
                            <span className="flex-1 text-sm font-medium">{it.label}</span>
                            <ChevronRight size={16} className="text-slate-400" />
                        </Link>
                    ))}
                </div>

                {/* Language switcher */}
                <div className="bg-white rounded-2xl border border-slate-200/60 product-shadow overflow-hidden">
                    <button
                        onClick={() => setLangOpen((o) => !o)}
                        className="w-full flex items-center gap-3 px-4 py-3.5 text-left tap-feedback"
                        data-testid="biz-lang-switcher-btn"
                    >
                        <div className="size-9 rounded-lg bg-violet-50 text-violet-600 flex items-center justify-center flex-shrink-0">
                            <Globe size={18} />
                        </div>
                        <span className="flex-1 text-sm font-medium">{t("common.language")}</span>
                        <span className="text-xs font-bold text-slate-500">{currentLang.flag} {currentLang.label}</span>
                        <ChevronRight size={16} className={`text-slate-400 transition-transform ${langOpen ? "rotate-90" : ""}`} />
                    </button>
                    {langOpen && (
                        <div className="border-t border-slate-100">
                            {LANGUAGES.map((l) => (
                                <button
                                    key={l.code}
                                    onClick={() => { setLang(l.code); setLangOpen(false); }}
                                    className={`w-full flex items-center gap-3 px-4 py-3 text-left text-sm hover:bg-slate-50 ${l.code === lang ? "bg-violet-50 font-semibold" : ""}`}
                                    data-testid={`biz-lang-option-${l.code}`}
                                >
                                    <span className="text-lg">{l.flag}</span>
                                    <span className="flex-1">{l.label}</span>
                                    {l.code === lang && <Check size={16} className="text-violet-600" />}
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                <button
                    onClick={() => { logout(); nav("/login"); }}
                    data-testid="biz-logout-btn"
                    className="w-full bg-white rounded-2xl border border-slate-200/60 px-4 py-3.5 flex items-center gap-3 text-rose-600 font-semibold tap-feedback"
                >
                    <div className="size-9 rounded-lg bg-rose-50 flex items-center justify-center">
                        <LogOut size={18} />
                    </div>
                    <span className="flex-1 text-left text-sm">Logout</span>
                    <ChevronRight size={16} className="text-rose-300" />
                </button>
            </div>
        </div>
    );
}


function TrustedTraderStatusCard({ tt }) {
    const earned = tt.is_trusted_trader;
    const need = Math.max(0, tt.criteria_min_orders - tt.qualified_orders);
    return (
        <div
            className={`rounded-2xl p-4 border product-shadow ${earned ? "border-emerald-200" : "border-slate-200/60 bg-white"}`}
            style={earned ? {
                background: "linear-gradient(135deg, #10B981 0%, #0EA5E9 50%, #6366F1 100%)",
            } : undefined}
            data-testid="trusted-trader-status-card"
        >
            {earned ? (
                <div className="text-white">
                    <div className="flex items-center justify-between">
                        <TrustedTraderBadge size="md" withTooltip={false} />
                        <Award size={20} className="opacity-70" />
                    </div>
                    <div className="font-display text-lg font-semibold mt-3">Trusted Trader unlocked</div>
                    <p className="text-xs text-white/85 mt-1 leading-relaxed">
                        {tt.qualified_orders} qualified bulk order{tt.qualified_orders === 1 ? "" : "s"} · avg group score <span className="font-bold">{tt.avg_group_score}</span> · zero overdue events.
                    </p>
                    <div className="mt-3 text-[11px] text-white/80">
                        Your marketplace profile now displays this badge to buyers — a verified trust signal that lifts repeat orders.
                    </div>
                </div>
            ) : (
                <div>
                    <div className="flex items-center justify-between">
                        <div className="text-xs uppercase tracking-wider font-bold text-slate-500 flex items-center gap-1.5">
                            <Award size={14} className="text-amber-500" /> Trusted Trader
                        </div>
                        <span className="text-[10px] font-bold uppercase tracking-wider bg-amber-50 text-amber-700 border border-amber-200 rounded-full px-2 py-0.5">Locked</span>
                    </div>
                    <p className="text-xs text-slate-600 mt-2 leading-relaxed">
                        Complete {need} more bulk-credit order{need === 1 ? "" : "s"} with a Group Credit DNA score ≥ {tt.criteria_min_score} and zero overdue payments to unlock the Trusted Trader badge.
                    </p>
                    <div className="mt-3 grid grid-cols-3 gap-2 text-[10px]">
                        <Stat label="Qualified" value={`${tt.qualified_orders}/${tt.criteria_min_orders}`} good={tt.qualified_orders >= tt.criteria_min_orders} />
                        <Stat label="Avg score" value={tt.avg_group_score || "—"} good={tt.avg_group_score && tt.avg_group_score >= tt.criteria_min_score} />
                        <Stat label="Overdue" value={tt.overdue_events} good={tt.overdue_events === 0} />
                    </div>
                </div>
            )}
        </div>
    );
}

function Stat({ label, value, good }) {
    return (
        <div className={`rounded-lg p-2 border ${good ? "bg-emerald-50 border-emerald-200" : "bg-slate-50 border-slate-200"}`}>
            <div className="text-slate-500 font-bold uppercase tracking-wider text-[9px]">{label}</div>
            <div className={`font-display font-bold text-base mt-0.5 ${good ? "text-emerald-700" : "text-slate-700"}`}>{value}</div>
        </div>
    );
}
